import {
  Flex,
  IconButton,
  Text,
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Image,
  Heading,
  Tooltip,
  Link,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaTelegramPlane } from "react-icons/fa";
import { MoonIcon, SunIcon, SearchIcon } from "@chakra-ui/icons";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useState, useEffect } from "react";
import { apiUrl } from "../utils/api";
import { Link as RouterLink } from "react-router-dom";

export default function MobileNav({
  colorMode,
  toggleColorMode,
  toggleSearchBar,
  showSearchBar,
  searchTerm,
  handleSearch,
  countdownToNoon,
  prizePool,
}) {
  const [isActive, setIsActive] = useState(false);

  const shellBg = useColorModeValue("whiteAlpha.900", "blackAlpha.600");
  const shellBorder = useColorModeValue("blackAlpha.200", "whiteAlpha.300");
  const inputColor = useColorModeValue("gray.900", "whiteAlpha.900");
  const placeholderColor = useColorModeValue("gray.500", "whiteAlpha.600");
  const iconColor = useColorModeValue("gray.600", "whiteAlpha.800");
  const barBg = useColorModeValue("rgba(255,255,255,0.85)", "rgba(0,0,0,0.45)");

  const barBorder = useColorModeValue(
    "rgba(0,0,0,0.1)",
    "rgba(255,255,255,0.18)",
  );

  const barShadow = useColorModeValue(
    "0 -6px 30px rgba(0,0,0,0.08)",
    "0 -6px 30px rgba(0,0,0,0.35)",
  );

  const shortAddr = (address) =>
    address ? `${address.slice(0, 4)}…${address.slice(-3)}` : "";

  useEffect(() => {
    let cancelled = false;

    const checkHealth = async () => {
      try {
        const response = await fetch(apiUrl("/health"), {
          credentials: "include",
        });

        if (!cancelled) {
          setIsActive(response.ok);
        }
      } catch {
        if (!cancelled) {
          setIsActive(false);
        }
      }
    };

    checkHealth();

    const interval = setInterval(checkHealth, 10000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      <Flex
        display={{ base: "flex", md: "none" }}
        justify="space-between"
        align="center"
        mb={8}
      >
        <Flex align="center" gap={2}>
          <Link as={RouterLink} to="/">
            <Image src="/lotoro-logo.png" alt="Lotoro Logo" boxSize="40px" />
          </Link>

          <Link as={RouterLink} to="/">
            <Heading fontSize="3xl">Lotoro</Heading>
          </Link>

          <Tooltip
            label={isActive ? "Status: Active" : "Status: Not Active"}
            hasArrow
            placement="top"
          >
            <Box
              w="8px"
              h="8px"
              borderRadius="full"
              bg={isActive ? "green.400" : "red.500"}
              boxShadow="0 0 4px rgba(0,0,0,.4)"
              mt="1px"
            />
          </Tooltip>
        </Flex>

        <Flex align="center" gap={1.5}>
          <IconButton
            as={Link}
            href="https://t.me/lotoroco"
            isExternal
            size="sm"
            variant="ghost"
            color="current"
            icon={<FaTelegramPlane />}
            aria-label="Telegram"
          />

          <IconButton
            size="sm"
            variant="ghost"
            color="current"
            onClick={toggleColorMode}
            icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
            aria-label="Toggle theme"
          />

          <ConnectButton.Custom>
            {({
              account,
              chain,
              mounted,
              openAccountModal,
              openConnectModal,
            }) => {
              const ready = mounted;
              const connected = ready && account && chain;

              const btnBg = useColorModeValue("blue.500", "blue.400");
              const btnHover = useColorModeValue("blue.600", "blue.500");
              const borderCol = useColorModeValue(
                "blackAlpha.200",
                "whiteAlpha.300",
              );

              if (!connected) {
                return (
                  <Button
                    size="sm"
                    onClick={openConnectModal}
                    bg={btnBg}
                    _hover={{ bg: btnHover }}
                    color="white"
                    rounded="lg"
                    px={3}
                    minW="auto"
                    lineHeight="1"
                    whiteSpace="nowrap"
                    border="1px solid"
                    borderColor={borderCol}
                  >
                    Connect
                  </Button>
                );
              }

              return (
                <Button
                  onClick={openAccountModal}
                  size="sm"
                  variant="outline"
                  borderColor={borderCol}
                  _hover={{
                    bg: useColorModeValue("blackAlpha.50", "whiteAlpha.100"),
                  }}
                  px={3}
                  minW="auto"
                  rounded="lg"
                  lineHeight="1"
                  whiteSpace="nowrap"
                >
                  {shortAddr(account.address)}
                </Button>
              );
            }}
          </ConnectButton.Custom>
        </Flex>
      </Flex>

      <Flex
        display={{ base: "flex", md: "none" }}
        justify="space-between"
        align="center"
        position="fixed"
        bottom="4"
        left="2"
        right="2"
        px="4"
        py="3"
        maxH="42px"
        bg={barBg}
        backdropFilter="blur(16px)"
        border="1px solid"
        borderColor={barBorder}
        borderRadius="xl"
        boxShadow={barShadow}
        zIndex="sticky"
      >
        <Text fontSize="sm" fontWeight="semibold">
          💰 Pool:{" "}
          <Box as="span" fontWeight="bold">
            ${(prizePool + 11500).toLocaleString()}
          </Box>
        </Text>

        <IconButton
          size="md"
          fontSize="lg"
          variant="ghost"
          color="current"
          onClick={toggleSearchBar}
          icon={<SearchIcon />}
          aria-label="Toggle Search"
        />

        <Text fontSize="sm" fontWeight="semibold">
          🕓 Payout:{" "}
          <Box as="span" fontWeight="bold">
            {countdownToNoon}
          </Box>
        </Text>
      </Flex>

      {showSearchBar && (
        <Box
          display={{ base: "block", md: "none" }}
          position="fixed"
          bottom="72px"
          left="0"
          right="0"
          px="6"
          zIndex="overlay"
        >
          <Box
            maxW="280px"
            mx="auto"
            bg={shellBg}
            border="1px solid"
            borderColor={shellBorder}
            borderRadius="lg"
            p="1.5"
            boxShadow="md"
            backdropFilter="blur(6px)"
          >
            <InputGroup size="sm" maxW="220px" mx="auto">
              <InputLeftElement pointerEvents="none" h="full">
                <SearchIcon color={iconColor} boxSize={4} />
              </InputLeftElement>

              <Input
                type="text"
                size="sm"
                pl="32px"
                h="34px"
                placeholder="Search a number"
                value={searchTerm}
                onChange={handleSearch}
                bg="transparent"
                color={inputColor}
                _placeholder={{ color: placeholderColor }}
                borderRadius="md"
                borderWidth="0"
                textAlign="center"
                _focus={{
                  boxShadow: "none",
                  borderColor: "transparent",
                }}
              />
            </InputGroup>
          </Box>
        </Box>
      )}
    </>
  );
}
